import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { SeoHead } from "@/components/SeoHead";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { LogOut, Save, Megaphone } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const Admin = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [promoEnabled, setPromoEnabled] = useState(true);
  const [promoText, setPromoText] = useState("");
  const [promoTextMobile, setPromoTextMobile] = useState("");
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate("/admin/login");
        return;
      }

      const { data: isAdmin } = await supabase.rpc("has_role", {
        _user_id: session.user.id,
        _role: "admin",
      });

      if (!isAdmin) {
        await supabase.auth.signOut();
        navigate("/admin/login");
        return;
      }

      // Load settings
      const { data: settings } = await supabase
        .from("site_settings")
        .select("key, value");

      if (settings) {
        const map = Object.fromEntries(settings.map((s) => [s.key, s.value]));
        setPromoEnabled(map.promo_enabled === "true");
        setPromoText(map.promo_text || "");
        setPromoTextMobile(map.promo_text_mobile || "");
      }

      setLoading(false);
    };

    checkAuth();
  }, [navigate]);

  const handleSave = async () => {
    setSaving(true);
    const updates = [
      { key: "promo_enabled", value: promoEnabled ? "true" : "false" },
      { key: "promo_text", value: promoText },
      { key: "promo_text_mobile", value: promoTextMobile },
    ];

    for (const update of updates) {
      await supabase
        .from("site_settings")
        .update({ value: update.value })
        .eq("key", update.key);
    }

    setSaving(false);
    toast({ title: "Settings saved", description: "Your changes are now live on the website." });
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/admin/login");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary/30">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary/30">
      <SeoHead title="Admin Dashboard | Semper Fi Pump Service" description="Admin dashboard" noindex />

      {/* Top bar */}
      <div className="bg-background border-b border-border">
        <div className="container flex items-center justify-between h-14">
          <h1 className="text-lg font-bold text-foreground">Site Admin</h1>
          <Button variant="ghost" size="sm" onClick={handleLogout}>
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </div>

      <div className="container py-8 max-w-2xl">
        {/* Promo Banner Settings */}
        <div className="bg-background rounded-xl shadow-sm border border-border p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-accent/10">
              <Megaphone className="w-5 h-5 text-accent" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-foreground">Promo Banner</h2>
              <p className="text-sm text-muted-foreground">Control the promotional banner at the top of the site</p>
            </div>
          </div>

          <div className="space-y-5">
            <div className="flex items-center justify-between">
              <Label htmlFor="promo-toggle" className="text-sm font-medium">
                Banner Visible
              </Label>
              <Switch
                id="promo-toggle"
                checked={promoEnabled}
                onCheckedChange={setPromoEnabled}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="promo-text" className="text-sm font-medium">
                Desktop Text <span className="text-muted-foreground font-normal">(HTML supported)</span>
              </Label>
              <Input
                id="promo-text"
                value={promoText}
                onChange={(e) => setPromoText(e.target.value)}
                placeholder="Enter promo text..."
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="promo-mobile" className="text-sm font-medium">
                Mobile Text <span className="text-muted-foreground font-normal">(shorter version)</span>
              </Label>
              <Input
                id="promo-mobile"
                value={promoTextMobile}
                onChange={(e) => setPromoTextMobile(e.target.value)}
                placeholder="Enter mobile promo text..."
              />
            </div>

            {/* Preview */}
            {promoEnabled && (
              <div className="rounded-lg border border-border overflow-hidden">
                <p className="text-xs font-medium text-muted-foreground px-3 py-1.5 bg-muted/50">Preview</p>
                <div className="bg-primary text-primary-foreground py-2.5 px-4 text-[13px] font-medium">
                  <span dangerouslySetInnerHTML={{ __html: promoText }} />
                </div>
              </div>
            )}

            <Button onClick={handleSave} disabled={saving} className="w-full">
              <Save className="w-4 h-4 mr-2" />
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Admin;
